import React, { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardShellProvider } from '../context/DashboardShellContext';
import BaseDashboardLayout from './BaseDashboardLayout';
import { getFacultyNavSectionsWithCapabilities } from '../components/Sidebar/roleNavConfig';
import { buildDomainSections } from '../components/Sidebar/buildDomainSections';
import { FACULTY_ACTIVITY_FEED_PATH, FACULTY_WORKSPACE_OVERVIEW_PATH } from '../constants/facultyNav';
import useEffectiveCapabilities from '../hooks/useEffectiveCapabilities';

// RBAC Migration (Phase 4): Replace legacy role checks with capability-based authorization
const CODING_PROGRAMME_COORDINATOR_CAPABILITY = 'MANAGE_BATCHES';
const TEACHING_CAPABILITY = 'VIEW_BATCH_DASHBOARD';
const ACTIVITY_FEED_CAPABILITY = 'RUN_CODING_ANALYTICS_SYNC';

const FacultyLayout = () => {
  const { user } = useAuth();
  const { effectiveCapabilities } = useEffectiveCapabilities();
  const { pathname } = useLocation();


  const baseNavSections = useMemo(
    () => getFacultyNavSectionsWithCapabilities({ user, effectiveCapabilities }),
    [user, effectiveCapabilities]
  );
  const sections = useMemo(() => buildDomainSections(baseNavSections, 'faculty'), [baseNavSections]);
  const isWideCoachingHub = /^\/faculty\/student\/[^/]+$/.test(pathname);
  const shell = useMemo(() => {
    // RBAC Migration: Check capabilities instead of roles
    const isCbcFaculty = effectiveCapabilities?.includes(CODING_PROGRAMME_COORDINATOR_CAPABILITY) ?? false;
    const hasCodingNav = effectiveCapabilities?.includes(TEACHING_CAPABILITY) ?? false;
    return {
      homePath: '/faculty/dashboard',
      workspacePath: hasCodingNav ? FACULTY_WORKSPACE_OVERVIEW_PATH : '/faculty/dashboard',
      profilePath: '/faculty/profile',
      projectBasePath: '/faculty/project',
      codingRoot: hasCodingNav ? '/faculty' : null,
      codingActivityPath: isCbcFaculty && effectiveCapabilities?.includes(ACTIVITY_FEED_CAPABILITY) ? FACULTY_ACTIVITY_FEED_PATH : null,
    };
  }, [effectiveCapabilities]);

  /** Absolute path — sidebar brand always goes to the faculty teaching workbench. */
  const brandPath = '/faculty/dashboard';

  return (
    <DashboardShellProvider value={shell}>
      <BaseDashboardLayout
        brandPath={brandPath}
        sections={sections}
        contentMaxWidth={isWideCoachingHub ? false : 'lg'}
      >
        <Outlet />
      </BaseDashboardLayout>
    </DashboardShellProvider>
  );
};

export default FacultyLayout;
